import React, { memo, useState } from 'react'
import { AppInput, Label } from '../../components'
import { InferLabelFromName } from '../../utils'
import numeral from 'numeral'
import { Engine } from 'yatte'

const NumberComponent = ({
  id,
  name,
  helpText,
  label,
  style,
  value,
  minimum,
  maximum,
  places,
  context,
  onChange,
  unlabeled,
}) => {
  const [strVal, setStrVal] = useState('')
  const [errMsg, setErrMsg] = useState('')
  let min = minimum && minimum.trim() ? Engine.compileExpr(minimum) : undefined
  let minVal = min && context.scope.evaluate(min)
  let max = maximum && maximum.trim() ? Engine.compileExpr(maximum) : undefined
  let maxVal = max && context.scope.evaluate(max)
  // check for sensible values in min/max
  if (min && max) {
    if (minVal > maxVal || (minVal === 0 && maxVal === 0)) {
      // don't enforce seemingly unintentional or conflicting limits
      min = max = minVal = maxVal = undefined
    }
  }
  if (typeof places === 'string' && places.length > 0) {
    places = Number.parseInt(places)
  }
  if (Number.isNaN(places)) {
    places = 0
  }
  if (unlabeled) {
    helpText = null
    label = null
  }
  const decDelimiter = numeral.localeData().delimiters.decimal 
  const entryFilterString = '0-9'
    + numeral.localeData().delimiters.thousands
    + ((places > 0) ? decDelimiter : '')
    + ((!minimum || minVal < 0) ? '-' : '')
  const entryFilterRegex = new RegExp(`[^${entryFilterString}]`, 'g')

  const change = str => {
    let numVal, nVal, err
    // get numeric value
    if (typeof str === 'string') {
      if (str === '') {
        nVal = null
      } else {
        str = str.trimLeft().replace(entryFilterRegex, '') // filter out all characters invalid in this numeric input
        numVal = numeral(str)
        let decPos = -1
        if (places > 0 && (decPos = str.indexOf(decDelimiter)) >= 0) {
          const maxLen = decPos + places + 1
          if (str.length > maxLen) {
            const valStr = numVal.format(defaultDisplayFormat) // round
            // const valStr = str.substring(0, maxLen) // truncate
            err = `→ ${valStr}`
            numVal = numeral(valStr)
          }
        }
        nVal = numVal.value()
      }
    }
    // enforce min/max
    if (nVal !== null && !isNaN(nVal)) {
      if (max) {
        if (nVal > maxVal) {
          nVal = maxVal
          if (style === 'spinbox') {
            str = nVal.toString()
          } else {
            err = `max → ${nVal.toString()}`
          }
        }
      }
      if (min) {
        if (nVal < minVal) {
          nVal = minVal
          if (style === 'spinbox') {
            str = nVal.toString()
          } else {
            err = `min → ${nVal.toString()}`
          }
        }
      }
    }
    // retain string value for editing continuity
    setStrVal(str)
    setErrMsg(err || '')
    if (isFinite(nVal)) {
      onChange(nVal)
    }
  }

  const defaultDisplayFormat = '0,0' +
    ((places > 0) ? ('.' + '0'.repeat(places)) : '')

  const handleFormat = str => {
    if (str) {
      // reset state
      setStrVal('')
      setErrMsg('')
      // format number nicely in field
      const n = numeral(str)
      return n.format(defaultDisplayFormat)
    }
  }

  switch (style) {
    case 'hidden':
      return null
    case 'spinbox':
      return (
        <div>
          <AppInput key={id} id={id} helpText={helpText} name={name}
                    label={label}
                    value={strVal || value}
                    onChange={val => change(val)} onFormat={handleFormat}
                    min={min ? minVal : null}
                    max={max ? maxVal : null}
                    type={'number'}/>
        </div>
      )

    case 'slider':
      return (
        <div>
          <Label name={name} helpText={helpText}
                 label={(label || InferLabelFromName(name)) +
                   ` (${value || '--'})`}
                 forId={id}/>
          <input type="range" className="custom-range"
                 value={value}
                 min={minVal || 0}
                 max={maxVal || 100}
                 onChange={event => onChange(parseInt(event.target.value))}
                 id={id}/>
        </div>
      )

    default: // includes case 'textbox'
      return (
        <div>
          <AppInput key={id} id={id} helpText={helpText} name={name}
                    label={label}
                    value={strVal || String(value)}
                    onChange={val => change(val)} onFormat={handleFormat}
                    type={'text'}/>
          {errMsg ? <span className={'text-danger'}>{errMsg}</span> : null}
        </div>
      )
  }
}

export default memo(NumberComponent)
